import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  MessageCircle, Ticket, BookOpen, BarChart3, Plus, Edit2, Trash2,
  Check, X, Clock, AlertTriangle, Search, ChevronRight, Eye,
  ArrowLeft, Save, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ======================== FAQ MANAGER TAB ========================
function FAQManager({ authHeader }) {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ question: '', answer: '', category: 'general', tags: '' });

  const fetchFaqs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support/faqs${filterCat ? `?category=${filterCat}` : ''}`);
      setFaqs(res.data.faqs);
      setCategories(res.data.categories);
    } catch (e) { toast.error('Failed to load FAQs'); }
  }, [filterCat]);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const saveFaq = async () => {
    const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      if (editing) {
        await axios.put(`${API}/support/faqs/${editing}`, payload, authHeader);
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API}/support/faqs`, payload, authHeader);
        toast.success('FAQ created');
      }
      setEditing(null); setCreating(false);
      setForm({ question: '', answer: '', category: 'general', tags: '' });
      fetchFaqs();
    } catch (e) { toast.error('Failed to save FAQ'); }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/support/faqs/${id}`, authHeader);
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const startEdit = (faq) => {
    setEditing(faq.id);
    setCreating(true);
    setForm({ question: faq.question, answer: faq.answer, category: faq.category, tags: (faq.tags || []).join(', ') });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <select
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
            data-testid="faq-category-filter"
          >
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <Badge variant="outline" className="text-slate-300">{faqs.length} FAQs</Badge>
        </div>
        <Button onClick={() => { setCreating(true); setEditing(null); setForm({ question: '', answer: '', category: 'general', tags: '' }); }} className="bg-emerald-600 hover:bg-emerald-500" data-testid="add-faq-btn">
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </div>

      {creating && (
        <GlassCard className="border-emerald-500/30">
          <h3 className="text-white font-semibold mb-4">{editing ? 'Edit FAQ' : 'New FAQ'}</h3>
          <div className="space-y-3">
            <Input value={form.question} onChange={e => setForm(prev => ({ ...prev, question: e.target.value }))} placeholder="Question" className="bg-slate-800 border-slate-700 text-white" data-testid="faq-question-input" />
            <textarea value={form.answer} onChange={e => setForm(prev => ({ ...prev, answer: e.target.value }))} placeholder="Answer" rows={3} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm resize-none" data-testid="faq-answer-input" />
            <div className="flex gap-3">
              <select value={form.category} onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm">
                <option value="billing">Billing</option>
                <option value="account">Account</option>
                <option value="features">Features</option>
                <option value="troubleshooting">Troubleshooting</option>
                <option value="refunds">Refunds</option>
                <option value="general">General</option>
              </select>
              <Input value={form.tags} onChange={e => setForm(prev => ({ ...prev, tags: e.target.value }))} placeholder="Tags (comma-separated)" className="bg-slate-800 border-slate-700 text-white flex-1" />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveFaq} className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-faq-btn"><Save className="w-4 h-4 mr-2" /> Save</Button>
              <Button variant="ghost" onClick={() => { setCreating(false); setEditing(null); }} className="text-slate-300">Cancel</Button>
            </div>
          </div>
        </GlassCard>
      )}

      <div className="space-y-2">
        {faqs.map(faq => (
          <GlassCard key={faq.id} className="!p-4 hover:border-emerald-500/30">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="text-xs" variant="outline">{faq.category}</Badge>
                  {!faq.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                </div>
                <p className="text-white font-medium text-sm">{faq.question}</p>
                <p className="text-slate-300 text-xs mt-1 line-clamp-2">{faq.answer}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => startEdit(faq)} className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-300 hover:text-white"><Edit2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => deleteFaq(faq.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-slate-300 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}

// ======================== TICKETS TAB ========================
function TicketsManager({ authHeader }) {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [conversation, setConversation] = useState([]);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support/tickets${filterStatus ? `?status=${filterStatus}` : ''}`, authHeader);
      setTickets(res.data.tickets);
      setStats(res.data.stats);
    } catch (e) { toast.error('Failed to load tickets'); }
  }, [filterStatus, authHeader]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API}/support/tickets/${ticketId}`, { status }, authHeader);
      toast.success(`Ticket ${status}`);
      fetchTickets();
    } catch (e) { toast.error('Failed to update'); }
  };

  const viewConversation = async (ticket) => {
    setSelectedTicket(ticket);
    if (ticket.session_id) {
      try {
        const res = await axios.get(`${API}/support/conversations/${ticket.session_id}`, authHeader);
        setConversation(res.data.messages);
      } catch (e) { setConversation([]); }
    }
  };

  const priorityColors = { low: 'bg-slate-500', medium: 'bg-amber-500', high: 'bg-red-500', urgent: 'bg-red-600' };
  const statusColors = { open: 'text-amber-400', in_progress: 'text-blue-400', resolved: 'text-emerald-400', closed: 'text-slate-300' };

  if (selectedTicket) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => setSelectedTicket(null)} className="text-slate-300 gap-2"><ArrowLeft className="w-4 h-4" /> Back to Tickets</Button>
        <GlassCard>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold">{selectedTicket.subject}</h3>
              <p className="text-slate-300 text-sm">{selectedTicket.name} • {selectedTicket.email}</p>
            </div>
            <div className="flex gap-2">
              {selectedTicket.status === 'open' && <Button size="sm" onClick={() => updateTicketStatus(selectedTicket.id, 'in_progress')} className="bg-blue-600">Mark In Progress</Button>}
              {selectedTicket.status !== 'resolved' && <Button size="sm" onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')} className="bg-emerald-600">Resolve</Button>}
            </div>
          </div>
          <p className="text-slate-300 text-sm mb-4">{selectedTicket.description}</p>
          {conversation.length > 0 && (
            <div className="border-t border-slate-700 pt-4 mt-4">
              <h4 className="text-slate-300 text-xs uppercase mb-3">Conversation History</h4>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {conversation.map((msg, i) => (
                  <div key={i} className={`p-2 rounded-lg text-sm ${msg.role === 'user' ? 'bg-blue-600/10 text-blue-200 ml-8' : 'bg-slate-800 text-slate-300 mr-8'}`}>
                    <span className="text-xs text-slate-300 block mb-1">{msg.role === 'user' ? 'Customer' : 'Bot'}</span>
                    {msg.content}
                  </div>
                ))}
              </div>
            </div>
          )}
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Open', count: stats.open || 0, color: 'amber' },
          { label: 'In Progress', count: stats.in_progress || 0, color: 'blue' },
          { label: 'Resolved', count: stats.resolved || 0, color: 'emerald' },
          { label: 'Total', count: stats.total || 0, color: 'slate' },
        ].map(s => (
          <div key={s.label} className={`p-3 rounded-xl bg-${s.color}-500/10 border border-${s.color}-500/20 text-center`}>
            <p className={`text-2xl font-bold text-${s.color}-400`}>{s.count}</p>
            <p className="text-xs text-slate-300">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        {['', 'open', 'in_progress', 'resolved'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${filterStatus === s ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
            {s === '' ? 'All' : s.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {tickets.map(ticket => (
          <GlassCard key={ticket.id} className="!p-4 cursor-pointer hover:border-emerald-500/30" onClick={() => viewConversation(ticket)}>
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${priorityColors[ticket.priority]}`} />
                  <span className={`text-xs font-medium ${statusColors[ticket.status]}`}>{ticket.status?.replace('_', ' ')}</span>
                  <Badge variant="outline" className="text-xs">{ticket.category}</Badge>
                </div>
                <p className="text-white text-sm font-medium truncate">{ticket.subject}</p>
                <p className="text-slate-300 text-xs">{ticket.name} • {ticket.email} • {new Date(ticket.created_at).toLocaleDateString()}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </div>
          </GlassCard>
        ))}
        {tickets.length === 0 && <p className="text-slate-300 text-sm text-center py-8">No tickets found</p>}
      </div>
    </div>
  );
}

// ======================== ANALYTICS TAB ========================
function AnalyticsView({ authHeader }) {
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/support/analytics`, authHeader);
        setAnalytics(res.data);
      } catch (e) { toast.error('Failed to load analytics'); }
    })();
  }, [authHeader]);

  if (!analytics) return <div className="flex justify-center py-8"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Conversations', value: analytics.total_conversations, color: 'blue' },
          { label: 'Messages', value: analytics.total_messages, color: 'emerald' },
          { label: 'Escalations', value: analytics.escalation_count, color: 'amber' },
          { label: 'FAQs', value: analytics.faq_count, color: 'purple' },
        ].map(s => (
          <GlassCard key={s.label} className="!p-4 text-center">
            <p className={`text-3xl font-bold text-${s.color}-400`}>{s.value}</p>
            <p className="text-xs text-slate-300 mt-1">{s.label}</p>
          </GlassCard>
        ))}
      </div>

      {analytics.category_breakdown?.length > 0 && (
        <GlassCard>
          <h3 className="text-white font-semibold mb-4">Categories</h3>
          <div className="space-y-2">
            {analytics.category_breakdown.map(cat => (
              <div key={cat.category} className="flex items-center justify-between">
                <span className="text-slate-300 text-sm capitalize">{cat.category}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, (cat.count / (analytics.total_messages || 1)) * 100)}%` }} />
                  </div>
                  <span className="text-slate-300 text-xs w-8 text-right">{cat.count}</span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {analytics.recent_sessions?.length > 0 && (
        <GlassCard>
          <h3 className="text-white font-semibold mb-4">Recent Conversations</h3>
          <div className="space-y-2">
            {analytics.recent_sessions.map(session => (
              <div key={session.session_id} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="text-slate-300 text-sm truncate">{session.last_message}</p>
                  <p className="text-slate-300 text-xs">{session.user_email || 'Anonymous'} • {session.message_count} messages</p>
                </div>
                <span className="text-slate-300 text-xs flex-shrink-0 ml-3">{new Date(session.last_time).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
}

// ======================== MAIN PAGE ========================
export default function SupportAdminPage() {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('analytics');
  const authHeader = getAuthHeader();

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'tickets', label: 'Tickets', icon: Ticket },
    { id: 'faqs', label: 'FAQs', icon: BookOpen },
  ];

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="font-outfit text-3xl font-semibold text-white mb-2">Support Center</h1>
        <p className="text-slate-300">Manage FAQs, view conversations, and handle escalation tickets</p>
      </div>

      <div className="flex gap-2 mb-6">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`support-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/60 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'analytics' && <AnalyticsView authHeader={authHeader} />}
      {activeTab === 'tickets' && <TicketsManager authHeader={authHeader} />}
      {activeTab === 'faqs' && <FAQManager authHeader={authHeader} />}
    </AppLayout>
  );
}
